/**
 * DEVLOGS-TEMP — HTMLMediaElement instrumentation (web + desktop renderer).
 *
 * Prototype patching, not a MutationObserver: elements that never play are never
 * instrumented, so there is nothing to observe for.
 *
 * `play()` MUST RETURN THE ORIGINAL PROMISE OBJECT. Handlers are attached to it;
 * a `.then()` derivative is NOT returned. artifacts/web/src/player/lib/
 * audio-mixer.ts awaits this promise inside generation-guard logic
 * (`gen !== this.playGeneration`), and inserting a microtask can reorder that
 * check. A rejected play() is also the single highest-value web signal there is
 * — NotAllowedError (autoplay blocked) versus AbortError (superseded by a new
 * load) are indistinguishable from the events alone.
 *
 * Element identity matters: two crossfading audio elements are otherwise
 * indistinguishable in the log, and the crossfade path is exactly where the bugs
 * are. Snapshot key names are copied VERBATIM from `snapshotAudioElement`
 * (artifacts/web/src/player/lib/player-debug.ts) so old `player.debug`
 * device_events rows and new devlog rows are queryable together.
 */

import { LEVELS } from "../level.ts";
import { TimeupdateCoalescer } from "../coalesce.ts";
import { sanitizeUrl } from "../redact.ts";
import type { EmitFn } from "../types.ts";

export interface MediaCaptureOptions {
  emit: EmitFn;
  now: () => number;
  progressWindowMs?: number;
}

const PATCH_TAG = Symbol.for("omnimedia.devlog.media");

const EVENTS = [
  "loadstart",
  "loadedmetadata",
  "loadeddata",
  "canplay",
  "canplaythrough",
  "play",
  "playing",
  "pause",
  "waiting",
  "stalled",
  "suspend",
  "abort",
  "emptied",
  "seeking",
  "seeked",
  "ratechange",
  "volumechange",
  "ended",
  "error",
] as const;

interface ElementState {
  id: string;
  coalescer: TimeupdateCoalescer;
  attached: boolean;
}

export function installMediaCapture(
  scope: { HTMLMediaElement?: typeof HTMLMediaElement },
  options: MediaCaptureOptions,
): () => void {
  const Media = scope.HTMLMediaElement;
  if (!Media || typeof Media.prototype?.play !== "function") return () => {};
  const proto = Media.prototype as unknown as Record<PropertyKey, unknown>;
  if (proto[PATCH_TAG]) return () => {};

  const progressWindowMs = options.progressWindowMs ?? 5000;
  const state = new WeakMap<object, ElementState>();
  let nextId = 1;

  function stateFor(el: HTMLMediaElement): ElementState {
    const existing = state.get(el);
    if (existing) return existing;
    const created: ElementState = {
      id: `m${nextId}`,
      coalescer: new TimeupdateCoalescer(progressWindowMs, options.now),
      attached: false,
    };
    nextId += 1;
    state.set(el, created);
    return created;
  }

  function ns(el: HTMLMediaElement): string {
    return safeCall(() => (el.tagName === "VIDEO" ? "media.video" : "media.audio"), "media.audio");
  }

  function attach(el: HTMLMediaElement): void {
    const entry = stateFor(el);
    if (entry.attached) return;
    entry.attached = true;

    safeVoid(() => {
      for (const type of EVENTS) {
        el.addEventListener(type, () => {
          safeVoid(() => {
            // Any real event closes the progress window first, so the summary
            // lands immediately before the thing it explains.
            const summary = entry.coalescer.flush();
            if (summary) {
              options.emit(LEVELS.debug, ns(el), "progress", {
                elId: entry.id,
                ...summary,
                ...snapshot(el),
              });
            }
            const level = type === "error" ? LEVELS.error : type === "stalled" || type === "waiting" ? LEVELS.warn : LEVELS.debug;
            options.emit(level, ns(el), type, { elId: entry.id, ...snapshot(el) });
          });
        });
      }

      el.addEventListener("timeupdate", () => {
        safeVoid(() => {
          const summary = entry.coalescer.tick(safeCall(() => el.currentTime, 0));
          if (summary) {
            options.emit(LEVELS.debug, ns(el), "progress", {
              elId: entry.id,
              ...summary,
              ...snapshot(el),
            });
          }
        });
      });
    });
  }

  // --- play() -------------------------------------------------------------
  const originalPlay = Media.prototype.play;
  Media.prototype.play = function (this: HTMLMediaElement): Promise<void> {
    const entry = stateFor(this);
    attach(this);
    safeVoid(() =>
      options.emit(LEVELS.debug, ns(this), "play.call", { elId: entry.id, ...snapshot(this) }),
    );
    const promise = originalPlay.call(this);
    // Attach observers WITHOUT changing what is returned.
    safeVoid(() => {
      promise.then(
        () => {
          safeVoid(() =>
            options.emit(LEVELS.debug, ns(this), "play.resolved", { elId: entry.id }),
          );
        },
        (error: unknown) => {
          safeVoid(() =>
            options.emit(
              LEVELS.warn,
              ns(this),
              "play.rejected",
              { elId: entry.id, ...snapshot(this) },
              { err: error },
            ),
          );
        },
      );
    });
    return promise; // the ORIGINAL promise object
  };

  // --- pause() / load() ---------------------------------------------------
  const originalPause = Media.prototype.pause;
  Media.prototype.pause = function (this: HTMLMediaElement): void {
    safeVoid(() =>
      options.emit(LEVELS.debug, ns(this), "pause.call", {
        elId: stateFor(this).id,
        ...snapshot(this),
      }),
    );
    return originalPause.call(this);
  };

  const originalLoad = Media.prototype.load;
  Media.prototype.load = function (this: HTMLMediaElement): void {
    safeVoid(() =>
      options.emit(LEVELS.debug, ns(this), "load.call", { elId: stateFor(this).id }),
    );
    return originalLoad.call(this);
  };

  // --- src setter ---------------------------------------------------------
  // Who assigned a source, and when, is what makes the crossfade path readable.
  const srcDescriptor = Object.getOwnPropertyDescriptor(Media.prototype, "src");
  if (srcDescriptor?.set && srcDescriptor.get) {
    const originalSet = srcDescriptor.set;
    const originalGet = srcDescriptor.get;
    safeVoid(() => {
      Object.defineProperty(Media.prototype, "src", {
        configurable: true,
        enumerable: srcDescriptor.enumerable,
        get: originalGet,
        set(this: HTMLMediaElement, value: string) {
          safeVoid(() =>
            options.emit(LEVELS.debug, ns(this), "src.set", {
              elId: stateFor(this).id,
              url: sanitizeUrl(value),
              hadSrc: safeCall(() => Boolean(this.currentSrc), false),
            }),
          );
          originalSet.call(this, value);
        },
      });
    });
  }

  proto[PATCH_TAG] = true;

  return () => {
    Media.prototype.play = originalPlay;
    Media.prototype.pause = originalPause;
    Media.prototype.load = originalLoad;
    if (srcDescriptor) safeVoid(() => Object.defineProperty(Media.prototype, "src", srcDescriptor));
    delete proto[PATCH_TAG];
  };
}

/**
 * Key names copied verbatim from `snapshotAudioElement` so the two generations
 * of telemetry join. `srcHost` only — never the signed URL.
 */
function snapshot(el: HTMLMediaElement): Record<string, unknown> {
  return {
    paused: safeCall(() => el.paused, undefined),
    muted: safeCall(() => el.muted, undefined),
    volume: safeCall(() => round2(el.volume), undefined),
    currentTime: safeCall(() => round2(el.currentTime), undefined),
    duration: safeCall(() => round2(el.duration), undefined),
    readyState: safeCall(() => el.readyState, undefined),
    networkState: safeCall(() => el.networkState, undefined),
    errorCode: safeCall(() => el.error?.code, undefined),
    errorMessage: safeCall(() => el.error?.message, undefined),
    srcHost: safeCall(() => hostOf(el.currentSrc), undefined),
    ended: safeCall(() => el.ended, undefined),
    playbackRate: safeCall(() => el.playbackRate, undefined),
    buffered: safeCall(() => ranges(el.buffered, 3), undefined),
  };
}

function ranges(list: TimeRanges | undefined, max: number): Array<[number, number]> | undefined {
  if (!list) return undefined;
  const out: Array<[number, number]> = [];
  const count = Math.min(list.length, max);
  for (let i = 0; i < count; i += 1) {
    out.push([round2(list.start(i)), round2(list.end(i))]);
  }
  return out;
}

function hostOf(url: string | undefined): string | undefined {
  if (!url) return undefined;
  try {
    return new URL(url).host;
  } catch {
    return undefined;
  }
}

function round2(value: number): number {
  if (!Number.isFinite(value)) return 0;
  return Math.round(value * 100) / 100;
}

function safeVoid(fn: () => void): void {
  try {
    fn();
  } catch {
    /* fire-and-forget */
  }
}

function safeCall<T>(fn: () => T, fallback: T): T {
  try {
    return fn();
  } catch {
    return fallback;
  }
}
